import React from 'react'
import app, { auth } from '../lib/firebase'
import { getFirestore, getDoc, doc, collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { Folder, Plus, Search, Calendar, FileText, Microscope, ChevronRight, Home } from 'lucide-react'
import { useAppSelector, useAppDispatch } from '../store/hooks'
import { store } from '../store'
import { 
  // UI actions
  toggleSidebar,
  toggleActivityPanel,
  toggleBottomPanel,
  setBottomPanelTab,
  setSearchQuery,
  openResearchTab,
  showToast,
  hideToast,
  addActivityLog,
  setBottomPanelCollapsed,
} from '../store/slices/uiSlice'
import {
  // Tab actions
  openTab,
  closeTab,
  setActiveTab
} from '../store/slices/tabsSlice'
import {
  // Notes actions
  addNote,
  updateNote,
  deleteNote,
  type Note,
} from '../store/slices/notesSlice'
import {
  // Documents actions
  clearDocumentData,
} from '../store/slices/documentsSlice'
// Import selectors from the selectors folder
import {
  // UI selectors
  selectSidebarCollapsed,
  selectActivityPanelCollapsed,
  selectBottomPanelCollapsed,
  selectBottomPanelTab,
  selectSearchQuery,
  selectActivityLog,
  selectToast,
  // Cases selectors
  selectFilteredCases,
  selectCaseById,
  selectCaseStats,
  // Documents selectors
  selectExtractedData,
  selectTransactions,
  selectValidationResult,
  // Notes selectors
  selectAllNotes,
  // Settings selectors
  // selectSettings, // Not used
} from '../store/selectors'
// import {
//   updateSetting,
// } from '../store/slices/settingsSlice'

// Components
import Sidebar, { type SidebarItem } from '../components/app/layout/Sidebar'
import { selectCase } from '../store/slices/casesSlice'
import { 
  setSelectedCaseId as setSidebarSelectedCaseId,
  addDocumentToCase,
  removeDocument,
  updateDocumentStatus 
} from '../store/slices/sidebarSlice'
import { removeWorkItem } from '../store/slices/workSlice'
import { clearAnalysis, selectDocument as selectAnalysisDocument } from '../store/slices/analysisSlice'
import ActionStateView from '../components/app/common/ActionStateView'
import { startUpload } from '../store/slices/documentProcessingSlice'
import NoteDialog from '../components/app/notes/NoteDialog'
import TabBar from '../components/app/tabs/TabBar'
import AIAssist from '../components/app/chat/AIAssist'
import ValidationView from '../components/app/validation/ValidationView'
import DataView from '../components/app/data/DataView'
import TransactionsView from '../components/app/data/TransactionsView'
import NotesPanel from '../components/app/notes/NotesPanel'
import { EnhancedFileViewer } from '../components/app/common/EnhancedFileViewer'
import { ActivityCenter } from '../components/ActivityCenter'
//import SettingsView from '../components/app/common/SettingsView'
import SummaryView from '../components/app/summary/SummaryView'
import ResearchView from '../components/app/research/ResearchView'
import Toast from '../components/app/common/Toast'
import JsonViewer from '../components/app/common/JsonViewer'
import { useFeatures, FeatureGuard } from '../hooks/useFeatures'
import FeatureTogglePanel from '../components/dev/FeatureTogglePanel'
import { functionsClient } from '../lib/functionsClient'
import { useAuth } from '../contexts/AuthContext'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { workService } from '../services/workService'

import feneroLogo from '../assets/logo-orange.svg'

const AppPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const features = useFeatures()
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const { workId: workIdFromUrl, documentId: documentIdFromUrl } = useParams<{ workId?: string; documentId?: string }>()
  
  console.log('📍 AppPage URL Params:', { workIdFromUrl, documentIdFromUrl })
  
  // Redirect to login if not authenticated
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  // UI State from Redux
  const sidebarCollapsed = useAppSelector(selectSidebarCollapsed)
  const activityCollapsed = useAppSelector(selectActivityPanelCollapsed)
  const bottomPanelCollapsed = useAppSelector(selectBottomPanelCollapsed)
  const bottomTab = useAppSelector(selectBottomPanelTab)
  const activeTabId = useAppSelector(state => state.tabs.activeTabId)
  const selectedCaseId = useAppSelector(state => state.cases.selectedCaseId)
  const openTabs = useAppSelector(state => state.tabs.openTabs)
  const searchQuery = useAppSelector(selectSearchQuery)
  const activityLog = useAppSelector(selectActivityLog)
  const toast = useAppSelector(selectToast)
  const activeTab = useAppSelector(state => state.tabs.openTabs.find(t => t.id === state.tabs.activeTabId) || null)
  
  // Domain State from Redux
  const filteredCases = useAppSelector(React.useMemo(
    () => selectFilteredCases(searchQuery), 
    [searchQuery]
  ))
  const notes = useAppSelector(selectAllNotes)
  const extractedData = useAppSelector(selectExtractedData)
  const transactions = useAppSelector(selectTransactions)
  const validationResult = useAppSelector(selectValidationResult)
  // const settings = useAppSelector(selectSettings) // Not used
  const caseStats = useAppSelector(selectCaseStats)
  
  // Get case for summary tab if active
  const activeSummaryCaseId = activeTab?.type === 'summary' 
    ? activeTab.id.replace('summary-', '') 
    : null
  const activeSummaryCase = useAppSelector(state => 
    activeSummaryCaseId ? selectCaseById(activeSummaryCaseId)(state) : null
  )
  
  // Local state for note dialog
  const [noteDialog, setNoteDialog] = React.useState<{
    isOpen: boolean
    category?: string
    title?: string  
  }>({ isOpen: false })
  
  // Local state for right panel resize - Responsive default width
  const [rightPanelWidth, setRightPanelWidth] = React.useState(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 1024) return 280 // Narrower on tablets
      if (window.innerWidth < 1280) return 300 // Slightly narrower on smaller desktops
      return 320 // Full width on larger screens
    }
    return 320
  })
  const [isResizingRight, setIsResizingRight] = React.useState(false)
  const [resizeStartX, setResizeStartX] = React.useState(0)
  const [resizeStartWidth, setResizeStartWidth] = React.useState(0)
  
  
  // Keep sidebar's selectedCaseId in sync with cases slice
  React.useEffect(() => {
    if (selectedCaseId !== undefined) {
      dispatch(setSidebarSelectedCaseId(selectedCaseId ?? null))
    }
  }, [dispatch, selectedCaseId])
  
  // Auto-collapse panels on small screens for better mobile UX
  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      
      // On tablet and below, auto-collapse sidebar and activity panel
      if (width < 1024) {
        if (!sidebarCollapsed) {
          dispatch(toggleSidebar())
        }
        if (!activityCollapsed) {
          dispatch(toggleActivityPanel())
        }
      }
    }
    
    // Check on mount
    handleResize()
    
    // Add resize listener
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, []) // Only run on mount

  // Subscribe to work items for this user
  React.useEffect(() => {
    if (user) {
      workService.subscribeToUserWork(user.uid)
      return () => workService.unsubscribeAll()
    }
  }, [user])
  
  // Update document status in sidebar when work status changes
  React.useEffect(() => {
    const workState = store.getState().work
    workState.ids.forEach(workId => {
      const workItem = workState.byId[workId]
      if (workItem?.subject?.documentId) {
        const status = workItem.status === 'completed' ? 'complete' : 
                       workItem.status === 'failed' ? 'error' : 
                       workItem.status === 'running' ? 'processing' : 'pending'
        dispatch(updateDocumentStatus({
          documentId: workItem.subject.documentId,
          status: status as any
        }))
      }
    })
  }, [dispatch, store])

  // Load documents from Firestore and add to sidebar
  React.useEffect(() => {
    const loadDocuments = async () => {
      if (!user) return  // Only need user, not selectedCaseId
      
      try {
        const db = getFirestore(app)
        const { getDocs, collection: firestoreCollection, query, where } = await import('firebase/firestore')
        
        // Query documents for the current user
        // If case is selected, filter by case_id; otherwise show all user documents
        const docsQuery = selectedCaseId
          ? query(
              firestoreCollection(db, 'documents'),
              where('uid', '==', user.uid),
              where('case_id', '==', selectedCaseId)
            )
          : query(
          firestoreCollection(db, 'documents'),
          where('uid', '==', user.uid)
        )
        
        const snapshot = await getDocs(docsQuery)
        snapshot.forEach(doc => {
          const data = doc.data()
          dispatch(addDocumentToCase({
            caseId: selectedCaseId,  // Can be null/undefined
            document: {
              id: `doc-${doc.id}`,
              label: data.fileName || 'Unnamed Document',
              documentId: doc.id,
              status: 'complete'
            }
          }))
        })
      } catch (error) {
        console.error('Error loading documents:', error)
      }
    }
    
    loadDocuments()
  }, [user, selectedCaseId, dispatch])
  
  // Hydrate UI state on mount from Firestore (user) with localStorage fallback
  React.useEffect(() => {
    const hydrate = async () => {
      try {
        const db = getFirestore(app)
        const user = auth.currentUser

        let persisted: any | null = null

        if (user) {
          const snap = await getDoc(doc(db, 'user_ui_state', user.uid))
          persisted = snap.exists() ? (snap.data()?.state ?? null) : null
        }

        if (!persisted && typeof window !== 'undefined') {
          try { persisted = JSON.parse(localStorage.getItem('uiState') || 'null') } catch {}
        }

        if (persisted) {
          // Restore tabs and layout
          if (persisted.activeTabId) dispatch(setActiveTab(persisted.activeTabId))
          if (persisted.openTabs && Array.isArray(persisted.openTabs)) {
            // Ensure active tab is included
            for (const t of persisted.openTabs) {
              dispatch(openTab({
                id: t.id,
                label: t.label,
                type: t.type,
                closable: !!t.closable,
                iconName: t.iconName,
                resource: t.resource,
              }))
            }
          }
          if (persisted.layout?.bottomPanelTab) dispatch(setBottomPanelTab(persisted.layout.bottomPanelTab))
          if (persisted.selectedCaseId) {
            dispatch(selectCase(persisted.selectedCaseId))
            dispatch(setSidebarSelectedCaseId(persisted.selectedCaseId))
          }
        }
      } catch {}
    }
    hydrate()
  }, [dispatch])

  
  // Keyboard navigation for tabs
  const handleTabKeyDown = React.useCallback((e: KeyboardEvent) => {
    // Ctrl/Cmd + Tab to cycle through tabs
    if ((e.ctrlKey || e.metaKey) && e.key === 'Tab') {
      e.preventDefault()
      const currentIndex = openTabs.findIndex(tab => tab.id === activeTabId)
      const nextIndex = e.shiftKey 
        ? (currentIndex - 1 + openTabs.length) % openTabs.length
        : (currentIndex + 1) % openTabs.length
      dispatch(setActiveTab(openTabs[nextIndex].id))
    }
    // Ctrl/Cmd + W to close current tab
    else if ((e.ctrlKey || e.metaKey) && e.key === 'w') {
      e.preventDefault()
      dispatch(closeTab(activeTabId))
    }
  }, [dispatch, activeTabId, openTabs])
  
  // Add keyboard listener
  React.useEffect(() => {
    window.addEventListener('keydown', handleTabKeyDown)
    return () => window.removeEventListener('keydown', handleTabKeyDown)
  }, [handleTabKeyDown])
  
  // Handle right panel resizing
  const handleRightPanelResizeStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizingRight(true)
    setResizeStartX(e.clientX)
    setResizeStartWidth(rightPanelWidth)
  }, [rightPanelWidth])

  const handleRightPanelResizeMove = React.useCallback((e: MouseEvent) => {
    if (!isResizingRight) return
    
    const deltaX = resizeStartX - e.clientX // Positive delta means dragging left (expanding)
    const newWidth = Math.max(240, Math.min(600, resizeStartWidth + deltaX)) // Min 240px, max 600px
    setRightPanelWidth(newWidth)
  }, [isResizingRight, resizeStartX, resizeStartWidth])

  const handleRightPanelResizeEnd = React.useCallback(() => {
    setIsResizingRight(false)
  }, [])

  // Set up resize listeners for right panel
  React.useEffect(() => {
    if (isResizingRight) {
      document.addEventListener('mousemove', handleRightPanelResizeMove)
      document.addEventListener('mouseup', handleRightPanelResizeEnd)
      
      return () => {
        document.removeEventListener('mousemove', handleRightPanelResizeMove)
        document.removeEventListener('mouseup', handleRightPanelResizeEnd)
      }
    }
  }, [isResizingRight, handleRightPanelResizeMove, handleRightPanelResizeEnd])
  
  // Get work items from Redux to know when they're loaded
  const workItems = useAppSelector(state => state.work.byId)
  const workItemIds = useAppSelector(state => state.work.ids)
  
  // Track if URL navigation has been handled to prevent multiple executions
  const urlNavigationHandled = React.useRef(false)

  // Handle URL-based navigation (when user opens /app/work/:workId directly or refreshes)
  React.useEffect(() => {
    // Only execute on initial load
    if (urlNavigationHandled.current) {
      return
    }
    // Wait for work items to be loaded (at least one work item or explicit empty state)
    if (workItemIds.length === 0 && !user) {
      console.log('⏳ Waiting for work items to load...')
      return
    }
    
    if (workIdFromUrl) {
      console.log('📍 Opening work item from URL:', workIdFromUrl)
      console.log('Available work IDs:', workItemIds)
      
      // Get work item from Redux
      const workItem = workItems[workIdFromUrl]
      
      if (workItem) {
        const documentId = workItem.subject?.documentId
        const fileName = workItem.subject?.fileName || 'Document'
        
        console.log('✅ Found work item:', { workId: workIdFromUrl, documentId, fileName })
        
        // Open tab if not already open
        const existingTab = openTabs.find(t => t.id === `work-${workIdFromUrl}`)
        if (!existingTab) {
          dispatch(openTab({
            id: `work-${workIdFromUrl}`,
            label: fileName,
            type: 'file',
            closable: true,
            resource: {
              type: 'document',
              id: documentId || workIdFromUrl,
              meta: {
                workId: workIdFromUrl
              }
            }
          }))
        }
        
        // Set as active tab
        dispatch(setActiveTab(`work-${workIdFromUrl}`))
        urlNavigationHandled.current = true
      } else {
        // Only warn if we have work items loaded but this one isn't found
        if (workItemIds.length > 0) {
          console.warn('⚠️ Work item not found in Redux:', workIdFromUrl)
          console.log('Available work IDs:', workItemIds)
          urlNavigationHandled.current = true
          dispatch(setActiveTab('home'))
          navigate('/app', { replace: true })
        } else {
          console.log('⏳ Still waiting for work items...')
        }
      }
    } else if (documentIdFromUrl) {
      console.log('📍 Opening document from URL:', documentIdFromUrl)
      // Legacy document URL support
      const workItem = workItemIds
        .map(id => workItems[id])
        .find(w => w?.subject?.documentId === documentIdFromUrl)
      
      if (workItem) {
        // Redirect to work URL
        navigate(`/app/work/${workItem.id}`, { replace: true })
      }
    }
  }, [workIdFromUrl, documentIdFromUrl, dispatch, navigate, openTabs, workItems, workItemIds, user])

  // Sync URL with activeTabId changes (activeTabId -> URL direction)
  React.useEffect(() => {
    if (!activeTabId) return

    if (activeTabId === 'home' && urlNavigationHandled.current) {
      dispatch(selectAnalysisDocument(null))
      navigate('/app', { replace: true })
    } else if (activeTabId.startsWith('work-')) {
      const workId = activeTabId.replace('work-', '')
      navigate(`/app/work/${workId}`, { replace: true })
    }
  }, [activeTabId, navigate])

  // Handlers
  const handleSidebarItemDelete = async (item: SidebarItem) => {
    const documentId = item.metadata?.documentId
    if (!documentId) return
    
    try {
      console.log('Deleting document:', documentId)
      const result = await functionsClient.deleteDocument(documentId)
      
      if (result.success) {
        // 1. Remove from sidebar
        dispatch(removeDocument({ documentId }))
        
        // 2. Find and close all related tabs
        const relatedWorkIds = workItemIds.filter(id => {
          const workItem = workItems[id]
          return workItem?.subject?.documentId === documentId
        })
        
        // Close work-based tabs (primary format)
        relatedWorkIds.forEach(workId => {
          const workTabId = `work-${workId}`
          dispatch(closeTab(workTabId))
          console.log(`🗙 Closed tab: ${workTabId}`)
        })
        
        // Also close document-based tab (fallback format)
        dispatch(closeTab(documentId))
        console.log(`🗙 Closed tab: ${documentId}`)

        // 3. Remove associated work items from Redux state
        relatedWorkIds.forEach(workId => {
          dispatch(removeWorkItem(workId))
        })
        
        console.log(`✅ Cleaned up ${relatedWorkIds.length} work items for document ${documentId}`)
        
        // 4. Clear analysis data (verification, validation, key metrics)
        dispatch(clearAnalysis(documentId))
        console.log(`✅ Cleared analysis/verification/validation for document ${documentId}`)
        
        // 5. Clear document data (extracted data, transactions, annotations)
        dispatch(clearDocumentData())
        console.log(`✅ Cleared document data and annotations`)
        
        // Show success toast
        dispatch(showToast({
          message: `Document "${item.label}" deleted successfully`,
          type: 'success'
        }))
   
      }
    } catch (error: any) {
      console.error('Failed to delete document:', error)
      dispatch(showToast({
        message: `Failed to delete document: ${error.message}`,
        type: 'error'
      }))
    }
  }

  const handleSidebarItemClick = (item: SidebarItem) => {
    switch (item.type) {
      case 'file':
        const documentId = item.metadata?.documentId || item.id
        // Get the work_id for this document from Redux
        const workItem = store.getState().work.ids
          .map(id => store.getState().work.byId[id])
          .find(w => w?.subject?.documentId === documentId)
        
        const workId = workItem?.id
        
        // Clear previous document state before opening new document
        dispatch(clearDocumentData())
        
        dispatch(openTab({
          id: workId ? `work-${workId}` : item.id,
          label: item.label,
          type: 'file',
          closable: true,
          resource: {
            type: 'document',
            id: documentId,
            meta: {
              workId: workId
            }
          }
        }))
        
        // Navigate to the work item URL if we have a work_id
        if (workId) {
          console.log('📍 Navigating to work item:', workId)
          navigate(`/app/work/${workId}`)
        } else {
          console.warn('⚠️ No work_id found for document:', documentId)
        }
        break
      case 'extract':
        dispatch(openTab({
          id: item.id,
          label: item.label,
          type: 'data',
          closable: true
        }))
        break
      case 'validation':
        dispatch(openTab({
          id: item.id,
          label: item.label,
          type: 'validation',
          closable: true
        }))
        break
      case 'summary':
        dispatch(openTab({
          id: item.id,
          label: item.label,
          type: 'summary',
          closable: true,
          resource: {
            type: 'validation' as const,
            id: item.id,
            meta: {
              categoryType: getCategoryTypeFromId(item.id)
            }
          }
        }))
        break
      case 'flag':
        dispatch(openTab({
          id: item.id,
          label: item.label,
          type: 'flag',
          closable: true
        }))
        break
      default:
        console.log('Clicked item:', item)
    }
  }

  // Helper function to determine category type from item id
  const getCategoryTypeFromId = (id: string): string => {
    if (id.includes('income')) return 'income'
    if (id.includes('assets')) return 'assets'
    if (id.includes('collateral')) return 'collateral'
    if (id.includes('capacity')) return 'capacity'
    if (id.includes('financial')) return 'default'
    return 'default'
  }
  
  
  const handleAddNote = (note: { content: string; category?: string; tags?: string[] }) => {
    dispatch(addNote({
      content: note.content,
      category: note.category,
      tags: note.tags,
      timestamp: new Date(),
      caseId: selectedCaseId || undefined
    }))
    dispatch(showToast({
      message: 'Note added successfully',
      type: 'success'
    }))
  }
  
  const handleEditNote = (id: string, content: string) => {
    dispatch(updateNote({ id, content }))
  }
  
  const handleDeleteNote = (id: string) => {
    dispatch(deleteNote(id))
    dispatch(showToast({
      message: 'Note deleted',
      type: 'success'
    }))
  }

  const handleNoteClick = (note: Note) => {
    // Create a tab for the note
    dispatch(openTab({
      id: `note-${note.id}`,
      label: `Note: ${note.content.substring(0, 30)}${note.content.length > 30 ? '...' : ''}`,
      type: 'notes',
      closable: true,
      resource: {
        type: 'work' as const,
        id: note.id,
        meta: {
          noteId: note.id
        }
      }
    }))
    dispatch(setActiveTab(`note-${note.id}`))
  }
  
  // Disabled - not used in current implementation
  // const handleSettingChange = (_key: string, _value: boolean) => {
  //   dispatch(updateSetting({ key: _key, value: _value }))
  // }

  // Validate PDF file
  const validatePDFFile = (file: File): { valid: boolean; error?: string } => {
    if (!file.type || file.type !== 'application/pdf') {
      return { valid: false, error: 'Only PDF files are supported' }
    }
    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      return { valid: false, error: 'File size must be less than 100MB' }
    }
    return { valid: true }
  }

  // Process uploaded file (used by both file input and drag-drop)
  const processUploadedFile = async (file: File) => {
    const validation = validatePDFFile(file)
    if (!validation.valid) {
      dispatch(showToast({
        message: validation.error || 'Invalid file',
        type: 'error'
      }))
      return
    }

    try {
      // Clear previous document state before uploading new document
      dispatch(clearDocumentData())
      
      // Generate a temporary document ID right away
      const tempDocId = `temp-${Date.now()}`
      
      // Create local file URL for immediate viewing
      const localFileUrl = URL.createObjectURL(file)
      
      // IMMEDIATELY open the tab with the local file - BEFORE ANY UPLOAD!
      dispatch(openTab({
        id: `doc-${tempDocId}`,
        label: file.name,
        type: 'file',
        closable: true,
        resource: {
          type: 'document',
          id: tempDocId,
          meta: {
            localFileUrl: localFileUrl  // Pass the local URL in meta so it shows immediately!
          }
        }
      }))
      
      // Show upload toast
      dispatch(showToast({
        message: `Starting processing for ${file.name}...`,
        type: 'info'
      }))

      console.log('Starting upload and extraction for:', file.name)

      
      
      // Call unified function (handles upload + extraction + work creation)
      const result = await functionsClient.uploadFile(file)
      if (!result.success) {
        throw new Error('Upload and extraction failed')
      }
      
      // Get work ID and document ID from response
      const workId = result.workId
      const documentId = result.documentId
      
      console.log('Upload complete, work ID:', workId, 'document ID:', documentId)
      
      // localFileUrl was already created above before upload
      console.log('AppPage: Using local file URL for continued viewing:', localFileUrl)
      
      // Open document tab with resource reference and file URL
      // Work item is created by Cloud Function
      // Close the temporary tab and open a new one with the work ID
      dispatch(closeTab(`doc-${tempDocId}`))
      dispatch(openTab({
        id: `work-${workId}`,
        label: file.name,
        type: 'file',
        closable: true,
        resource: {
          type: 'document',
          id: documentId,
          meta: {
            workId,  // Include work ID for routing
            localFileUrl  // Keep the local file URL for continued immediate rendering
          }
        }
      }))
      
      // Don't navigate away - stay on home page so user can upload more
      // navigate(`/app/work/${workId}`)
      
      // Add document to sidebar (always to All Documents, and to case if selected)
      dispatch(addDocumentToCase({
        caseId: selectedCaseId,  // Can be null/undefined
        document: {
          id: `doc-${documentId}`,
          label: file.name,
          documentId: documentId,
          status: 'processing'
        }
      }))

      dispatch(showToast({
        message: `Processing ${file.name}...`,
        type: 'success'
      }))

      // Open the document viewer tab and make it active immediately


    } catch (error) {
      console.error('Upload failed:', error)
      dispatch(showToast({
        message: `Failed to upload ${file.name}`,
        type: 'error'
      }))
    }
  }

  // File input handler
  const handleUploadDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    await processUploadedFile(file)
    // Reset input so same file can be uploaded again
    e.target.value = ''
  }

  // Drag and drop handlers
  const [isDragging, setIsDragging] = React.useState(false)
  const dragCounter = React.useRef(0)

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    dragCounter.current = 0

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      await processUploadedFile(file)
    }
  }
  
  const handleRerunValidation = () => {
    dispatch(addActivityLog('Re-running validation...'))
    // Implementation would go here
  }
  
  const handleEditField = (key: string, value: any) => {
    console.log('Editing field:', key, value)
    // Implementation would go here
  }
  
  const handleEditTransaction = (index: number, transaction: any) => {
    console.log('Editing transaction:', index, transaction)
    // Implementation would go here
  }
  
  const openCaseSummary = (caseId: string, caseName: string) => {
    dispatch(selectCase(caseId))
    dispatch(setSidebarSelectedCaseId(caseId))
    dispatch(openTab({
      id: `summary-${caseId}`,
      label: `${caseName} - Summary`,
      type: 'summary',
      closable: true
    }))
  }
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'bg-green-100 text-green-800'
      case 'processing': return 'bg-blue-100 text-blue-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Create a new case and open its summary
  const handleAddNewCase = React.useCallback(async () => {
    try {
      const db = getFirestore(app)
      const user = auth.currentUser

      const nowIso = new Date().toISOString()
      const newCase = {
        name: 'Untitled Case',
        type: 'Mortgage Application',
        status: 'pending' as const,
        date: nowIso.slice(0, 10),
        progress: 0,
        documents: 0,
        flags: 0,
        userId: user?.uid ?? null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }

      // Persist to Firestore
      const ref = await addDoc(collection(db, 'cases'), newCase)

      // Reflect in Redux
      const createdId = ref.id
      dispatch(selectCase(createdId))
      dispatch(setSidebarSelectedCaseId(createdId))
      dispatch(openTab({
        id: `summary-${createdId}`,
        label: `${newCase.name} - Summary`,
        type: 'summary',
        closable: true
      }))
    } catch (e) {
      console.error('Failed to create case', e)
      // Fallback: open a local summary tab so user can proceed
      const fallbackId = `local-${Date.now()}`
      dispatch(selectCase(fallbackId))
      dispatch(setSidebarSelectedCaseId(fallbackId))
      dispatch(openTab({
        id: `summary-${fallbackId}`,
        label: 'Untitled Case - Summary',
        type: 'summary',
        closable: true
      }))
    }
  }, [dispatch])
  
  // Render content based on active tab
  const renderTabContent = () => {
    if (!activeTab) return null
    
    // Show different home based on feature set
    if (activeTab.type === 'home') {
      if (!features.cases) {
        // Extraction mode - Simple document upload interface
        return (
          <div 
            className="flex-1 flex items-center justify-center p-8"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="text-center max-w-lg">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Document Extraction</h1>
              <p className="text-gray-600 mb-8">Upload documents to extract and annotate data with AI assistance</p>
              
              <div className={`
                border-2 border-dashed rounded-lg p-12 mb-6 transition-all duration-200
                ${isDragging 
                  ? 'border-orange-500 bg-orange-50 scale-105' 
                  : 'border-gray-300 hover:border-orange-400 bg-white'
                }
              `}>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleUploadDocument}
                  className="hidden"
                  id="home-upload-input"
                />
                <label
                  htmlFor="home-upload-input"
                  className="cursor-pointer flex flex-col items-center gap-6"
                >
                  <div className={`
                    w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200
                    ${isDragging 
                      ? 'bg-orange-200 scale-110' 
                      : 'bg-orange-100'
                    }
                  `}>
                    <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xl font-semibold text-gray-900">
                      {isDragging ? 'Drop PDF Here' : 'Upload Document'}
                    </div>
                    <div className="text-gray-500 mt-1">
                      {isDragging ? 'Release to upload' : 'PDF only • Drag & drop or click'}
                    </div>
                  </div>
                </label>
              </div>
              
              <div className="text-sm text-gray-500">
                <p className="mb-3">Supported document types:</p>
                <div className="flex justify-center gap-3">
                  <span className="px-3 py-1 bg-gray-100 rounded-md">Bank Statements (PDF)</span>
                </div>
              </div>
            </div>
          </div>
        )
      } else {
        // Full mode - Cases dashboard
        return (
          <div className="flex-1 p-8 overflow-auto">
            <div className="max-w-6xl mx-auto">
              {/* Header with Add Case button */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Cases</h1>
                  <p className="text-gray-600 mt-1">Manage loan applications and documents</p>
                </div>
                <button onClick={handleAddNewCase} className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded flex items-center space-x-2 font-medium shadow-sm transition-colors">
                  <Plus className="w-5 h-5" />
                  <span>Add New Case</span>
                </button>
              </div>

            {/* Search bar */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search cases by name, ID, or type..."
                  value={searchQuery}
                  onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 mb-8">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Cases</p>
                    <p className="text-2xl font-bold text-gray-900">{caseStats.total}</p>
                  </div>
                  <Folder className="w-8 h-8 text-gray-400" />
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">In Progress</p>
                    <p className="text-2xl font-bold text-blue-600">{caseStats.processing}</p>
                  </div>
                  <div className="w-8 h-8 text-blue-400">⏳</div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Completed</p>
                    <p className="text-2xl font-bold text-green-600">{caseStats.complete}</p>
                  </div>
                  <div className="w-8 h-8 text-green-400">✅</div>
                </div>
              </div>
            </div>

            {/* Cases grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCases.map(caseItem => (
                <div 
                  key={caseItem.id}
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => openCaseSummary(caseItem.id, caseItem.name)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-900">{caseItem.name}</h3>
                      <p className="text-sm text-gray-600">{caseItem.type}</p>
                      <p className="text-xs text-gray-500 mt-1">Case #{caseItem.id}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(caseItem.status)}`}>
                      {caseItem.status}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{caseItem.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full transition-all"
                        style={{ width: `${caseItem.progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Case details */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center text-gray-600">
                        <FileText className="w-4 h-4 mr-1" />
                        <span>{Array.isArray(caseItem.documents) ? caseItem.documents.length : caseItem.documents}</span>
                      </div>
                      {caseItem.flags > 0 && (
                        <div className="flex items-center text-yellow-600">
                          <span className="w-4 h-4 mr-1">⚠️</span>
                          <span>{caseItem.flags}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center text-gray-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span className="text-xs">{caseItem.date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Empty state if no cases match */}
            {filteredCases.length === 0 && (
              <div className="text-center py-12">
                <Folder className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No cases found</h3>
                <p className="text-gray-500">
                  {searchQuery ? 'Try adjusting your search terms' : 'Click "Add New Case" to get started'}
                </p>
              </div>
            )}

            {/* Quick stats */}
   
          </div>
        </div>
      )
    }
  }
    
    // Handle other tab types
    switch (activeTab.type) {
      case 'file':
        // Get document ID from resource reference
        const documentId = activeTab.resource?.id
        if (!documentId) {
          return <div className="p-4">No document selected</div>
        }
        
        // Pass local file URL if available for early rendering
        const localFileUrl = activeTab.resource?.meta?.localFileUrl
        console.log('AppPage: Rendering file tab with:', { documentId, localFileUrl })
        
        return <EnhancedFileViewer documentId={documentId} localFileUrl={localFileUrl} />
      case 'validation':
        return <ValidationView validationResult={validationResult || { isValid: false, balanceCheck: { beginningBalance: 0, endingBalance: 0, expectedEnding: 0, difference: 0, totalCredits: 0, totalDebits: 0, transactionCount: 0 }, issues: [] }} onRerun={handleRerunValidation} />
      case 'data':
        return <DataView extractedData={extractedData || {}} onEditField={handleEditField} />
      case 'transactions':
        return <TransactionsView transactions={transactions} onEditTransaction={handleEditTransaction} />
      case 'summary':
        // Check if it's a category tab or main summary
        const categoryType = activeTab.resource?.meta?.categoryType
        if (categoryType && categoryType !== 'default') {
          // Show action state for category tabs
          return (
            <ActionStateView
              title={activeTab.label}
              categoryType={categoryType as any}
              hasContent={false} // TODO: Check if category has existing content
              onUploadDocument={() => {
                // Trigger file upload for this category
                const fileInput = document.createElement('input')
                fileInput.type = 'file'
                fileInput.accept = '.pdf,.png,.jpg,.jpeg'
                fileInput.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0]
                  if (file) {
                    const uploadId = `upload-${Date.now()}`
                    dispatch(startUpload({
                      id: uploadId,
                      fileName: file.name,
                      fileSize: file.size,
                      mimeType: file.type,
                      category: categoryType as any,
                      caseId: selectedCaseId || 'MG-2025-001'
                    }))
                    // TODO: Actually upload the file
                  }
                }
                fileInput.click()
              }}
              onCreateNote={() => {
                setNoteDialog({
                  isOpen: true,
                  category: categoryType,
                  title: `Add Note to ${activeTab.label}`
                })
              }}
            />
          )
        }
        
        // Extract case ID from tab ID (format: summary-CASE_ID)
        const caseId = activeTab.id.replace('summary-', '')
        
        return (
          <SummaryView 
            caseSummary={activeSummaryCase || {
              id: caseId,
              caseId,
              name: 'Unknown Case',
              type: 'Unknown',
              status: 'pending' as const,
              date: new Date().toISOString(),
              progress: 0,
              documents: 0,
              flags: 0
            }}
            onUploadDocument={() => console.log('Upload document')}
            onCreateNote={() => {
              setNoteDialog({
                isOpen: true,
                title: 'Add Note to Case Summary'
              })
            }}
          />
        )
      case 'research':
        return (
          <ResearchView 
            onSaveToNotes={(content) => {
              handleAddNote({ content, category: 'research' })
              dispatch(setBottomPanelCollapsed(false))
              dispatch(setBottomPanelTab('notes'))
            }}
          />
        )
      case 'notes':
        const noteId = activeTab.resource?.meta?.noteId
        const note = notes.find(n => n.id === noteId)
        if (!note) return <div className="p-8 text-center text-gray-500">Note not found</div>
        
        return (
          <div className="flex-1 p-8 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded shadow-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-xl font-semibold text-gray-900 mb-2">Note Details</h1>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{note.timestamp.toLocaleString()}</span>
                      {note.category && <span>Category: {note.category}</span>}
                      {note.author && <span>Author: {note.author}</span>}
                    </div>
                  </div>
                  {note.metadata?.annotationId && (
                    <div className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                      Annotation: {note.metadata.annotationId}
                    </div>
                  )}
                </div>
                
                {/* Tags */}
                {note.tags && note.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {note.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Screenshot */}
                {note.metadata?.screenshot && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Screenshot</h3>
                    <img 
                      src={note.metadata.screenshot}
                      alt="Note screenshot"
                      className="max-w-full h-auto border border-gray-300 rounded shadow-sm"
                    />
                  </div>
                )}
                
                {/* Content */}
                <div className="prose prose-sm max-w-none">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Content</h3>
                  <div className="bg-gray-50 p-4 rounded border text-gray-800 whitespace-pre-wrap">
                    {note.content}
                  </div>
                </div>
                
                {/* Position Info for Annotations */}
                {note.metadata?.position && (
                  <div className="mt-6 p-4 bg-gray-50 rounded">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Annotation Details</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>Page: {note.metadata.position.page}</div>
                      <div>Position: ({note.metadata.position.x.toFixed(3)}, {note.metadata.position.y.toFixed(3)})</div>
                      <div>Size: {note.metadata.position.w.toFixed(3)} × {note.metadata.position.h.toFixed(3)}</div>
                      {note.metadata.extractedText && (
                        <div className="col-span-2">
                          <span className="font-medium">Extracted Text: </span>
                          "{note.metadata.extractedText}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      case 'json-extracted':
        const extractedJsonData = activeTab.resource?.meta?.data
        return (
          <div className="flex-1 p-8 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <JsonViewer 
                title={`Extracted Data - ${activeTab.label}`}
                data={extractedJsonData || { error: 'No extracted data available' }}
              />
            </div>
          </div>
        )
      case 'json-annotations':
        const annotationsData = activeTab.resource?.meta?.data
        return (
          <div className="flex-1 p-8 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <JsonViewer 
                title={`Annotations Data - ${activeTab.label}`}
                data={annotationsData || { error: 'No annotations available' }}
              />
            </div>
          </div>
        )
      default:
        return null
    }
  }
  
  return (
    <div className="h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={feneroLogo} alt="Fenero" className="h-8" />
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"' }}>Fenero</h1>
          </div>
          <div className="flex items-center space-x-4">
            <FeatureGuard feature="research">
              <button 
                onClick={() => dispatch(openResearchTab())}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-medium flex items-center space-x-2 transition-colors"
              >
                <Microscope className="w-4 h-4" />
                <span>Research</span>
              </button>
            </FeatureGuard>
            <span className="text-sm text-gray-600">{user.email || user.displayName || 'User'}</span>
            <FeatureGuard feature="reports">
              <button className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm font-medium">
                Export Report
              </button>
            </FeatureGuard>
            <button
              onClick={async () => {
                try {
                  await auth.signOut()
                  navigate('/login')
                } catch (error) {
                  console.error('Logout failed:', error)
                }
              }}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded text-sm font-medium transition-colors"
            >
              Log Out
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <FeatureGuard feature="sidebar">
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => dispatch(toggleSidebar())}
            onItemClick={handleSidebarItemClick}
            onItemDelete={handleSidebarItemDelete}
            selectedItemIds={React.useMemo(() => {
              const ids = new Set<string>()
              const t = activeTab
              if (!t) return ids
              // Only highlight what's currently in view (active tab)
              if (['file', 'data', 'validation', 'summary', 'flag'].includes(t.type)) {
                let idToHighlight = t.id
                // Map summary tab opened from dashboard to sidebar item id
                if (t.type === 'summary' && idToHighlight.startsWith('summary-')) {
                  idToHighlight = 'financial-summary'
                }
                ids.add(idToHighlight)
              }
              return ids
            }, [activeTab])}
          />
        </FeatureGuard>
        
        {/* Center Panel */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Tab Bar - Fixed height */}
          <div className="flex-shrink-0">
            <TabBar
              tabs={openTabs}
              activeTabId={activeTabId}
              onTabClick={(tabId) => dispatch(setActiveTab(tabId))}
              onTabClose={(tabId) => dispatch(closeTab(tabId))}
            />
          </div>
          
          {/* Main Content Area */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-auto">
              {renderTabContent()}
            </div>
            
            {/* Bottom Panel - Notes & Activity */}
            <NotesPanel
              activeTab={bottomTab}
              notes={notes}
              activityLog={activityLog}
              collapsed={bottomPanelCollapsed}
              onTabChange={(tab) => dispatch(setBottomPanelTab(tab))}
              onAddNote={(content) => handleAddNote({ content })}
              onEditNote={handleEditNote}
              onDeleteNote={handleDeleteNote}
              onNoteClick={handleNoteClick}
              onToggleCollapse={() => dispatch(toggleBottomPanel())}
            />
          </div>
        </div>
        
        {/* Right Panel - Activity Center */}
        <FeatureGuard feature="rightPanel">
          <div 
            className={`
              flex flex-col bg-gray-50 border-l border-gray-200 transition-all duration-300 relative
              ${activityCollapsed ? 'w-12' : ''}
            `}
            style={activityCollapsed ? {} : { width: `${rightPanelWidth}px` }}
          >
            {/* Resize Handle */}
            {!activityCollapsed && (
              <div
                className="absolute top-0 left-0 bottom-0 w-1 cursor-ew-resize hover:bg-orange-400 transition-colors z-10 group"
                onMouseDown={handleRightPanelResizeStart}
                title="Drag to resize panel"
              >
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-12 bg-gray-300 group-hover:bg-orange-400 rounded-full transition-colors" />
              </div>
            )}
            
            {/* Header */}
            <div className={`${activityCollapsed ? 'p-2' : 'p-3'} border-b border-gray-200`}>
              <div className={`flex items-center ${activityCollapsed ? 'justify-center' : 'justify-between'}`}>
                {!activityCollapsed && (
                  <div className="flex-1 min-w-0">
                    <h3 
                      className="font-semibold text-gray-900 truncate" 
                      title="Activity Center"
                    >
                      Activity Center
                    </h3>
                  </div>
                )}
                <button
                  onClick={() => dispatch(toggleActivityPanel())}
                  className="p-1 hover:bg-gray-200 rounded flex-shrink-0 transition-colors"
                  title={activityCollapsed ? 'Expand Activity Center' : 'Collapse Activity Center'}
                >
                  {activityCollapsed ? (
                    <ChevronRight className="w-4 h-4 rotate-180" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            
            {/* Activity Center Content */}
            {!activityCollapsed && <ActivityCenter />}
          </div>
        </FeatureGuard>
      </div>
      
      {/* Toast Notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => dispatch(hideToast())}
        />
      )}
      
      {/* Note Dialog */}
      <NoteDialog
        isOpen={noteDialog.isOpen}
        onClose={() => setNoteDialog({ isOpen: false })}
        onSave={handleAddNote}
        category={noteDialog.category}
        title={noteDialog.title}
      />
      
      {/* Development Feature Toggle Panel */}
      <FeatureTogglePanel />
      
    </div>
  )
}

export default AppPage
